import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Paper from '@material-ui/core/Paper';
import LinearProgress from '@material-ui/core/LinearProgress';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { statusInfo, normalizar } from '../../components/StatusChip';
import { CORES } from '../../theme';

/**
 * Cores do calendário (definidas com a gestão em 30/10/2026):
 *  - vermelho   : não confirmada e o dia da reunião já chegou (hoje ou passado)
 *  - amarelo    : ainda não confirmada, reunião futura
 *  - azul       : confirmada
 *  - verde      : realizada
 *  - verde-água : oportunidade
 *  - cinza      : declinada / não aprovada (riscada)
 */
const CORES_EVENTO = {
    atrasada: { rotulo: 'Não confirmada no dia', background: '#FBE6DF', color: '#8E2F12', borda: '#A63A1A' },
    pendente: { rotulo: 'Aguardando confirmação', background: '#FFF3DC', color: '#7A4A05', borda: '#9A6100' },
    confirmada: { rotulo: 'Confirmada', background: '#E3ECFB', color: '#1E3F8A', borda: '#1E5BC6' },
    realizada: { rotulo: 'Realizada', background: '#E4EFDF', color: '#1F4615', borda: '#2F6420' },
    oportunidade: { rotulo: 'Oportunidade', background: '#D9F2EC', color: '#0E5A4C', borda: '#14866D' },
    cancelada: { rotulo: 'Declinada / não aprovada', background: '#EEF1EC', color: '#56604F', borda: '#8A9384' },
};

const corDoEvento = (e, hojeIso) => {
    const s = normalizar(e.status);
    if (s.startsWith('realizad')) return CORES_EVENTO.realizada;
    if (s.startsWith('oportunidade')) return CORES_EVENTO.oportunidade;
    if (s.startsWith('confirmad')) return CORES_EVENTO.confirmada;
    if (s.startsWith('declinad') || s.startsWith('nao_aprovad')) return CORES_EVENTO.cancelada;
    // em branco, aguardando, reagendada, aguardando nova data...: ainda não confirmada
    return e._dia <= hojeIso ? CORES_EVENTO.atrasada : CORES_EVENTO.pendente;
};

// Executa as chamadas em lotes (a visão Mês faz uma chamada por dia útil, ~22).
const emLotes = async (itens, tamanho, fn) => {
    const saida = [];
    for (let i = 0; i < itens.length; i += tamanho) {
        // eslint-disable-next-line no-await-in-loop
        saida.push(...(await Promise.all(itens.slice(i, i + tamanho).map(fn))));
    }
    return saida;
};

const MAX_POR_DIA_MES = 3;

const hora = (texto) => {
    const m = /^(\d{1,2})/.exec(String(texto || ''));
    return m ? parseInt(m[1], 10) : null;
};

/**
 * Calendário de reuniões (visões Dia, Semana e Mês).
 * Sem endpoint novo: busca a listagem existente (agendamento-lead/listing) um dia por vez,
 * com o parâmetro data_reuniao que a tela já usa, mais os filtros da página.
 * Cada evento mostra empresa (lead), cliente/projeto e assessor; a cor segue o status de confirmação.
 */
const CalendarioReunioes = ({ filtros, buscaVersao }) => {
    const [visao, setVisao] = useState('semana');
    const [referencia, setReferencia] = useState(() => moment().locale('pt-br'));
    const [eventos, setEventos] = useState([]);
    const [carregando, setCarregando] = useState(false);

    const dias = useMemo(() => {
        if (visao === 'dia') return [referencia.clone().startOf('day')];
        if (visao === 'mes') {
            // grade de segunda a sexta cobrindo o mês inteiro
            const ini = referencia.clone().startOf('month').startOf('isoWeek');
            const fim = referencia.clone().endOf('month').endOf('isoWeek');
            const lista = [];
            for (const d = ini.clone(); d.isSameOrBefore(fim, 'day'); d.add(1, 'day')) {
                if (d.isoWeekday() <= 5) lista.push(d.clone());
            }
            return lista;
        }
        const seg = referencia.clone().startOf('isoWeek');
        return [0, 1, 2, 3, 4].map((i) => seg.clone().add(i, 'days'));
    }, [visao, referencia]);

    // na visão Mês, só busca os dias do próprio mês (os das bordas aparecem apagados)
    const diasComBusca = useMemo(
        () => (visao === 'mes' ? dias.filter((d) => d.month() === referencia.month()) : dias),
        [dias, visao, referencia]
    );

    const chaveFiltros = JSON.stringify(filtros || {});

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        emLotes(diasComBusca, 5, (d) => api.get('agendamento-lead/listing', {
            params: {
                ...(filtros || {}),
                data_reuniao: d.format('YYYY-MM-DD'),
                page: 1,
                pageSize: 200,
                id_assessor: localStorage.getItem('id_assessor'),
            },
        }).then(({ data }) => (data && data.data ? data.data : []).map((r) => ({ ...r, _dia: d.format('YYYY-MM-DD') }))))
            .then((listas) => { if (ativo) setEventos([].concat(...listas)); })
            .catch((e) => { if (ativo) { setEventos([]); toast.error(e.message || 'Não foi possível carregar o calendário'); } })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [diasComBusca, chaveFiltros, buscaVersao]);

    const horas = useMemo(() => {
        const encontradas = eventos.map((e) => hora(e.hora_reuniao)).filter((h) => h !== null);
        const ini = Math.min(8, ...encontradas);
        const fim = Math.max(18, ...encontradas);
        const lista = [];
        for (let h = ini; h <= fim; h += 1) lista.push(h);
        return lista;
    }, [eventos]);

    const semHora = eventos.filter((e) => hora(e.hora_reuniao) === null);
    const hoje = moment().format('YYYY-MM-DD');
    const passo = visao === 'dia' ? 'day' : (visao === 'mes' ? 'month' : 'week');
    let titulo;
    if (visao === 'dia') titulo = referencia.clone().locale('pt-br').format('dddd, D [de] MMMM [de] YYYY');
    else if (visao === 'mes') titulo = referencia.clone().locale('pt-br').format('MMMM [de] YYYY');
    else titulo = `${dias[0].format('D')} – ${dias[dias.length - 1].clone().locale('pt-br').format('D [de] MMMM [de] YYYY')}`;

    const abrirDia = (d) => {
        setReferencia(d.clone().locale('pt-br'));
        setVisao('dia');
    };

    const Evento = ({ e, compacto }) => {
        const info = statusInfo('confirmacao', e.status);
        const cor = corDoEvento(e, hoje);
        return (
            <Link
                to={'/agendamento-lead/' + e.idAgendamento + '?id_lead=' + e.lead_id}
                title={`${e.hora_reuniao || ''} · ${e.corporate_name || ''} · ${e.customersName || ''} · ${e.assessoresName || ''} · ${info.rotulo}`}
                style={{
                    display: 'flex', flexDirection: 'column', gap: 1, textDecoration: 'none',
                    background: cor.background, color: cor.color, borderLeft: `3px solid ${cor.borda}`,
                    borderRadius: 6, padding: '4px 7px', fontSize: 12, lineHeight: 1.3, marginBottom: 3, minWidth: 0,
                }}
            >
                <b style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: cor === CORES_EVENTO.cancelada ? 'line-through' : 'none' }}>
                    {e.hora_reuniao ? String(e.hora_reuniao).slice(0, 5) + ' · ' : ''}{e.corporate_name}
                </b>
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.customersName || '—'}</span>
                {!compacto && <span style={{ opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.assessoresName || 'Sem assessor'}</span>}
            </Link>
        );
    };

    const colunas = `56px repeat(${dias.length}, minmax(140px, 1fr))`;

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <Button variant="outlined" size="small" aria-label="Anterior" onClick={() => setReferencia((r) => r.clone().subtract(1, passo))}>‹</Button>
                <Button variant="outlined" size="small" onClick={() => setReferencia(moment().locale('pt-br'))}>Hoje</Button>
                <Button variant="outlined" size="small" aria-label="Próximo" onClick={() => setReferencia((r) => r.clone().add(1, passo))}>›</Button>
                <b style={{ fontSize: 16, textTransform: visao === 'mes' ? 'capitalize' : 'none' }}>{titulo}</b>
                <ButtonGroup size="small" style={{ marginLeft: 8 }} aria-label="Período">
                    <Button variant={visao === 'dia' ? 'contained' : 'outlined'} color={visao === 'dia' ? 'primary' : 'default'} onClick={() => setVisao('dia')}>Dia</Button>
                    <Button variant={visao === 'semana' ? 'contained' : 'outlined'} color={visao === 'semana' ? 'primary' : 'default'} onClick={() => setVisao('semana')}>Semana</Button>
                    <Button variant={visao === 'mes' ? 'contained' : 'outlined'} color={visao === 'mes' ? 'primary' : 'default'} onClick={() => setVisao('mes')}>Mês</Button>
                </ButtonGroup>
                <span style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: CORES.textoSuave }} aria-label="Legenda">
                    {Object.values(CORES_EVENTO).map((c) => (
                        <span key={c.rotulo}><i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c.borda, marginRight: 4 }} />{c.rotulo}</span>
                    ))}
                </span>
            </div>

            <div style={{ height: 4 }}>{carregando && <LinearProgress />}</div>

            {visao === 'mes' ? (
            <Paper variant="outlined" style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(150px, 1fr))', minWidth: 750 }}>
                    {['Seg', 'Ter', 'Qua', 'Qui', 'Sex'].map((n) => (
                        <div key={n} style={{ padding: '9px 10px', fontSize: 13, fontWeight: 600, color: CORES.textoSuave, borderBottom: `1px solid ${CORES.borda}` }}>{n}</div>
                    ))}
                    {dias.map((d) => {
                        const chave = d.format('YYYY-MM-DD');
                        const doMes = d.month() === referencia.month();
                        const ehHoje = chave === hoje;
                        const doDia = eventos
                            .filter((e) => e._dia === chave)
                            .sort((a, b) => String(a.hora_reuniao || '99').localeCompare(String(b.hora_reuniao || '99')));
                        const resto = doDia.length - MAX_POR_DIA_MES;
                        return (
                            <div key={chave} style={{ minHeight: 120, padding: 4, borderBottom: '1px solid #EEF1EC', borderLeft: '1px solid #EEF1EC', background: doMes ? '#fff' : '#FAFBF9', minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                    <button
                                        type="button"
                                        onClick={() => abrirDia(d)}
                                        aria-label={`Abrir ${d.clone().locale('pt-br').format('D [de] MMMM')}`}
                                        style={{
                                            all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 700, minWidth: 22, height: 22, borderRadius: 11,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            color: ehHoje ? '#fff' : (doMes ? CORES.texto : '#A3AA9E'), background: ehHoje ? CORES.verde : 'transparent',
                                        }}
                                    >
                                        {d.date()}
                                    </button>
                                    {doDia.length > 0 && <span style={{ fontSize: 11, color: CORES.textoSuave }}>{doDia.length}</span>}
                                </div>
                                {doDia.slice(0, MAX_POR_DIA_MES).map((e) => <Evento key={e.idAgendamento || e.id} e={e} compacto />)}
                                {resto > 0 && (
                                    <button type="button" onClick={() => abrirDia(d)} style={{ all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: CORES.verdeEscuro, padding: '0 4px' }}>
                                        + {resto} {resto === 1 ? 'reunião' : 'reuniões'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Paper>
            ) : (
            <Paper variant="outlined" style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: colunas, minWidth: 56 + dias.length * 140 }}>
                    <div style={{ borderBottom: `1px solid ${CORES.borda}` }} />
                    {dias.map((d) => {
                        const ehHoje = d.format('YYYY-MM-DD') === hoje;
                        return (
                            <div key={d.format('L')} style={{ padding: '9px 10px', fontSize: 13, fontWeight: 600, color: ehHoje ? CORES.verdeEscuro : CORES.textoSuave, background: ehHoje ? CORES.verdeClaro : undefined, borderBottom: `1px solid ${CORES.borda}`, borderLeft: `1px solid ${CORES.trilho || '#EEF1EC'}`, textTransform: 'capitalize' }}>
                                {d.clone().locale('pt-br').format('ddd D')}
                            </div>
                        );
                    })}
                    {horas.map((h) => (
                        <React.Fragment key={h}>
                            <div style={{ fontSize: 11, color: CORES.textoSuave, padding: '4px 8px', borderBottom: '1px solid #EEF1EC', minHeight: 48 }}>
                                {String(h).padStart(2, '0')}:00
                            </div>
                            {dias.map((d) => {
                                const chave = d.format('YYYY-MM-DD');
                                const doSlot = eventos
                                    .filter((e) => e._dia === chave && hora(e.hora_reuniao) === h)
                                    .sort((a, b) => String(a.hora_reuniao).localeCompare(String(b.hora_reuniao)));
                                return (
                                    <div key={chave + h} style={{ borderBottom: '1px solid #EEF1EC', borderLeft: '1px solid #EEF1EC', padding: 3, minHeight: 48, minWidth: 0 }}>
                                        {doSlot.map((e) => <Evento key={e.idAgendamento || e.id} e={e} />)}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </Paper>
            )}

            {visao !== 'mes' && semHora.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: CORES.textoSuave }}>
                    {semHora.length} reunião(ões) sem horário definido no período — veja na visão Lista.
                </div>
            )}
            {!carregando && eventos.length === 0 && (
                <div style={{ marginTop: 10, fontSize: 13, color: CORES.textoSuave }}>Nenhuma reunião neste período com os filtros atuais.</div>
            )}
        </div>
    );
};

export default CalendarioReunioes;
